import { useCallback, useContext, useEffect, useState } from 'react';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import {
  BIOMETRIC_ENROLLMENT_STATUS,
  useBiometricEnrollment,
} from '../../../assets/hooks/useBiometricEnrollment';
import { BiometricIcon } from '../../../assets/icons/icons';
import SuccessCheckBadge from '../../SuccessCheckBadge/SuccessCheckBadge';
import BottomActionButton from '../../shared/BottomActionButton/BottomActionButton';
import BottomSheet from '../../shared/BottomSheet/BottomSheet';
import './PostUnlockBiometricSheet.css';

export const POST_UNLOCK_BIOMETRIC_PROMPT_STATE_KEY = 'showPostUnlockBiometricPrompt';

const PROMPT_STATE = {
  OFFER: 'offer',
  SUCCESS: 'success',
  DENIED: 'denied',
};

function PostUnlockBiometricSheet({ onClose }) {
  const { common, profile, t } = useContext(TranslationContext);
  const enrollment = useBiometricEnrollment();
  const { refreshBiometricInfo, resetEnrollment } = enrollment;
  const [promptState, setPromptState] = useState(PROMPT_STATE.OFFER);
  const isSuccess = promptState === PROMPT_STATE.SUCCESS;
  const isDenied = promptState === PROMPT_STATE.DENIED;

  useEffect(() => {
    resetEnrollment();
    refreshBiometricInfo();
  }, [refreshBiometricInfo, resetEnrollment]);

  const handleClose = useCallback(() => {
    if (enrollment.isBusy) {
      return;
    }

    enrollment.resetEnrollment();
    onClose();
  }, [enrollment, onClose]);

  const handleEnable = useCallback(async () => {
    const result = await enrollment.startEnrollment();

    if (result.success) {
      setPromptState(PROMPT_STATE.SUCCESS);
      return;
    }

    if (result.reason === 'denied') {
      setPromptState(PROMPT_STATE.DENIED);
    }
  }, [enrollment]);

  const title = isSuccess
    ? profile.biometric.enabledTitle
    : isDenied
      ? profile.biometric.notEnabledTitle
      : enrollment.status === BIOMETRIC_ENROLLMENT_STATUS.ERROR
        ? profile.biometric.couldNotEnableTitle
        : t('profile.biometric.enableTitle', {
          label: enrollment.biometricLabel,
        });
  const subtitle = isSuccess
    ? t('profile.biometric.enabledSuccessText', {
      productName: common.appName,
      label: enrollment.biometricLabel,
    })
    : isDenied
      ? profile.biometric.notEnabledText
      : enrollment.status === BIOMETRIC_ENROLLMENT_STATUS.ERROR
        ? profile.biometric.couldNotEnableText
        : t('profile.biometric.enableText', {
          productName: common.appName,
          label: enrollment.biometricLabel,
        });
  const primaryLabel = isSuccess || isDenied
    ? common.done
    : enrollment.isBusy
      ? profile.biometric.settingUp
      : enrollment.status === BIOMETRIC_ENROLLMENT_STATUS.ERROR
        ? common.tryAgain
        : enrollment.enableButtonLabel;

  return (
    <BottomSheet
      className="post-unlock-biometric-sheet"
      panelClassName="post-unlock-biometric-sheet__panel"
      titleId="post-unlock-biometric-sheet-title"
      isDismissible={!enrollment.isBusy}
      onClose={handleClose}
    >
      <div className="post-unlock-biometric-sheet__content">
        <SuccessCheckBadge
          className="post-unlock-biometric-sheet__badge"
          icon={BiometricIcon}
        />
        <h2
          id="post-unlock-biometric-sheet-title"
          className="post-unlock-biometric-sheet__title"
        >
          {title}
        </h2>
        <p className="post-unlock-biometric-sheet__subtitle">{subtitle}</p>
      </div>

      <div className="post-unlock-biometric-sheet__actions">
        <BottomActionButton
          className="post-unlock-biometric-sheet__primary"
          onClick={isSuccess || isDenied ? handleClose : handleEnable}
          disabled={enrollment.isBusy}
        >
          {primaryLabel}
        </BottomActionButton>
        {!isSuccess && !isDenied && (
          <button
            type="button"
            className="post-unlock-biometric-sheet__later"
            onClick={handleClose}
            disabled={enrollment.isBusy}
          >
            {common.later}
          </button>
        )}
      </div>
    </BottomSheet>
  );
}

export default PostUnlockBiometricSheet;
